<template>
    <client-only>
        <div class="persons-table">
            <UserFilters
                v-model="filters"
                :has-active-filters="hasActiveFilters"
                @submit="applyFilters"
                @reset="resetFilters"
            />
            <v-data-table-server
                :headers="headers"
                :items="items"
                :items-per-page-options="[10, 15, 50, 100, -1]"
                :options="options"
                :loading="isLoading"
                :items-length="serverItemsLength"
                :hide-default-header="isMobile"
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="persons-table"
                must-sort
                @update:options="onUpdateTableOptions"
            >
                <template v-slot:item.NAME="{item}">
                    <span v-if="item.role === 'Support' && !isSupport">
                        {{ item.name || $t('user') }}
                    </span>
                    <nuxt-link
                        v-else
                        no-prefetch
                        :to="{
                            name: personType,
                            params: {id: item.id, ...(companyIdForSearch && {companyId: companyIdForSearch})},
                        }"
                        :data-cy="`link-person-page-${item.id}`"
                    >
                        <strong>{{ item.name || $t('user') }}</strong>
                    </nuxt-link>
                </template>
                <template v-slot:item.active="{item}">
                    <div class="d-flex justify-end">
                        <v-switch
                            v-model="item.active"
                            :disabled="dontAllowEdit"
                            color="primary"
                            hide-details
                            :label="item.active ? $t('active') : $t('inactive')"
                            :data-cy="`person-active-${item.id}`"
                            @update:model-value="changeActive(item)"
                        />
                    </div>
                </template>
                <template v-slot:item.mobile="{item}">
                    <div class="d-flex align-start full-width py-2">
                        <div class="d-flex flex-column text-caption">
                            <nuxt-link
                                no-prefetch
                                :to="{
                                    name: personType,
                                    params: {id: item.id, ...(companyIdForSearch && {companyId: companyIdForSearch})},
                                }"
                                :data-cy="`link-person-page-${item.id}`"
                            >
                                <strong>{{ item.name || $t('user') }}</strong>
                            </nuxt-link>
                            <span v-if="item.login" class="font-weight-medium">
                                {{ item.login }}
                            </span>
                            <span v-if="item.role">
                                {{ item.role }}
                            </span>
                            <span v-if="item.email">
                                {{ item.email }}
                            </span>
                        </div>
                        <v-spacer />
                        <div class="d-flex flex-column full-height justify-center align-end text-caption">
                            <div class="d-flex align-center mt-1 full-height">
                                <span class="text-no-wrap text-caption">
                                    {{ $t('active') }}
                                </span>
                                <v-switch
                                    v-model="item.active"
                                    :disabled="dontAllowEdit"
                                    color="primary"
                                    class="mx-2"
                                    density="compact"
                                    hide-details
                                    :data-cy="`person-active-${item.id}`"
                                    @update:model-value="changeActive(item)"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </v-data-table-server>
        </div>
    </client-only>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator'
    import {clean, clone, isEqual} from '~/utils/helpers'
    import UserFilters from '~/components/account/users/UserFilters.vue'
    import type {UserFiltersType} from '~/utils/types/account/users/types'
    import {initUserFilters} from '~/utils/types/account/users/helpers'
    import type {SelectOptionType} from '~/utils/types/types'

    @Component({
        components: {UserFilters},
    })
    export class PersonsTable extends Vue {
        @Prop() companyId
        @Prop() company
        @Prop() _headers
        @Prop({default: 'companyPerson', type: String}) personType

        options = {
            itemsPerPage: 10,
            page: 1,
            sortBy: [
                {
                    key: 'NAME',
                    order: 'asc',
                },
            ],
        }
        filters: UserFiltersType = initUserFilters()
        availablePersonRoles: SelectOptionType[] = []

        async mounted() {
            await this.$store.restored
            if (this.company?.type) await runtimeStore.loadAvailablePersonRoles(this.company.type)
        }

        async load(): Promise<void> {
            const {itemsPerPage, page, sortBy} = this.options
            let key = 'NAME'
            let order = 'asc'
            if (sortBy?.length) {
                key = sortBy[0].key
                order = sortBy[0].order
            }
            const filters = clone(this.filters)
            clean(filters)
            if (filters.name) Object.assign(filters, {pattern: filters.name})

            await runtimeStore.loadPersons({
                sortingParam: key,
                ascending: order === 'asc',
                ...filters,
                companyId: this.companyIdForSearch,
                shortResponse: false,
                limit: itemsPerPage,
                offset: itemsPerPage * (page - 1),
            })
        }

        initOptions(): void {
            this.options = {page: 1, itemsPerPage: this.options.itemsPerPage}
        }

        applyFilters(): void {
            this.initOptions()
            this.load()
        }

        resetFilters(): void {
            this.filters = initUserFilters()
            this.initOptions()
            this.load()
        }

        get hasActiveFilters(): boolean {
            return !isEqual(initUserFilters(), this.filters)
        }

        get headers() {
            if (this.isMobile) return [{value: 'mobile'}]

            const headers = [
                {title: this.$t('name'), value: 'name', sortable: true, key: 'NAME'},
                {title: this.$t('login'), value: 'login', sortable: true, key: 'LOGIN'},
                {title: this.$t('role'), value: 'role', sortable: true, key: 'ROLE'},
                {title: this.$t('email'), value: 'email', sortable: true, key: 'EMAIL'},
                {title: this.$t('skype'), value: 'skype', sortable: true, key: 'SKYPE'},
                {title: this.$t('status'), value: 'active', minWidth: '120px', align: 'end'},
            ]

            if (this._headers?.length) {
                const sortingArr = [
                    'name',
                    'login',
                    'role',
                    'department',
                    'email',
                    'phone',
                    'messenger',
                    'skype',
                    'active',
                ]

                return headers
                    .concat(this._headers)
                    .sort((a, b) => sortingArr.indexOf(a.value) - sortingArr.indexOf(b.value))
            }

            return headers
        }

        get items() {
            return runtimeStore.persons.map(person => {
                let firstName = person.firstName || ''
                let lastName = person.lastName || ''

                const role = person.role
                if (role && role?.includes('corp.')) {
                    firstName = person.firstNameLocalized || ''
                    lastName = person.lastNameLocalized || ''
                }

                return {
                    id: person.personId,
                    name: `${person.prefix || ''} ${firstName} ${lastName}`.trim(),
                    login: person.login,
                    role: this.$t(`roles.${person.role.split('.')[1]}`),
                    email: person.email,
                    phone: person.contactPhone,
                    active: person.active === 'true',
                    department: person?.department?.name,
                    messenger: person?.icq,
                    skype: person.skype || '',
                }
            })
        }

        get serverItemsLength() {
            return runtimeStore.personsCount
        }

        get isLoading() {
            return runtimeStore.loading
        }

        get companyIdForSearch() {
            if (this.companyId) {
                return this.companyId
            } else {
                return this.$route.query.company || authStore.person.companyId
            }
        }

        get dontAllowEdit() {
            return this.company?.type === 'HUB_SUPPLIER'
        }

        async changeActive(item) {
            const {id, active} = item
            try {
                await this.$api.persons.patch(id, {active})
                this.$toast.success(this.$t('success_message.saved_successfully'))
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                await this.load()
            }
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        onUpdateTableOptions(options) {
            this.options = options
            this.load()
        }

        get personRole() {
            return authStore.personRole
        }

        get isSupport() {
            return this.personRole === 'support'
        }
    }

    export default PersonsTable
</script>
